import React, { useState, useEffect } from "react";
import {
  Database,
  LogIn,
  AlertCircle,
  ArrowLeft,
  CheckCircle,
  Mail,
} from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";

const AUTH_URL = process.env.REACT_APP_AUTH_URL || "/auth";

const LoginPage = () => {
  const { login } = useAuth();
  const [view, setView] = useState("login"); // login | forgot | reset | reset-success
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [resetToken, setResetToken] = useState("");
  const [tokenValid, setTokenValid] = useState(null);

  // Check for reset token in URL
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get("token");
    if (token) {
      setResetToken(token);
      setView("reset");
      validateToken(token);
    }
  }, []);

  const validateToken = async (token) => {
    try {
      const res = await fetch(`${AUTH_URL}/validate-reset-token`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token }),
      });
      const data = await res.json();
      setTokenValid(data.valid);
      if (!data.valid)
        setError("This reset link has expired. Please request a new one.");
    } catch {
      setTokenValid(false);
      setError("Unable to validate reset link.");
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
    } catch (err) {
      setError(err.message);
    }
    setLoading(false);
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setError("");
    setMessage("");
    setLoading(true);
    try {
      const res = await fetch(`${AUTH_URL}/forgot-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, app_url: window.location.origin }),
      });
      const data = await res.json();
      if (res.ok) {
        setMessage(
          data.message ||
            "If an account with that email exists, a reset link has been sent.",
        );
      } else {
        setError(data.error || "Something went wrong.");
      }
    } catch {
      setError("Unable to send reset email. Try again later.");
    }
    setLoading(false);
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setError("");
    if (newPassword !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }
    if (newPassword.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${AUTH_URL}/reset-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token: resetToken, new_password: newPassword }),
      });
      const data = await res.json();
      if (res.ok) {
        setView("reset-success");
        // Clean URL
        window.history.replaceState({}, "", window.location.pathname);
      } else {
        setError(data.error || "Failed to reset password.");
      }
    } catch {
      setError("Unable to reset password. Try again later.");
    }
    setLoading(false);
  };

  const Header = () => (
    <div className="flex items-center justify-center mb-6">
      <img
        src="/logo-white.png"
        alt="Hyve Applied Intelligence"
        className="h-12"
        style={{ filter: "brightness(0) invert(20%)" }}
      />
    </div>
  );

  const ErrorBanner = () =>
    error ? (
      <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center">
        <AlertCircle className="w-4 h-4 text-red-500 mr-2 flex-shrink-0" />
        <span className="text-sm text-red-700">{error}</span>
      </div>
    ) : null;

  const MessageBanner = () =>
    message ? (
      <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg flex items-center">
        <CheckCircle className="w-4 h-4 text-green-500 mr-2 flex-shrink-0" />
        <span className="text-sm text-green-700">{message}</span>
      </div>
    ) : null;

  const renderLogin = () => (
    <>
      <Header />
      <ErrorBanner />
      <form onSubmit={handleLogin} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">
            Email
          </label>
          <input
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@company.com"
            autoComplete="email"
            className="w-full px-3 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">
            Password
          </label>
          <input
            type="password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Enter password"
            autoComplete="current-password"
            className="w-full px-3 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
          />
        </div>
        <button
          type="submit"
          disabled={loading}
          className="w-full py-2.5 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white rounded-lg font-medium text-sm transition-colors flex items-center justify-center"
        >
          {loading ? (
            "Signing in..."
          ) : (
            <>
              <LogIn className="w-4 h-4 mr-2" />
              Sign In
            </>
          )}
        </button>
      </form>
      <button
        onClick={() => {
          setView("forgot");
          setError("");
          setMessage("");
        }}
        className="w-full mt-3 text-sm text-blue-600 hover:text-blue-800 transition-colors"
      >
        Forgot your password?
      </button>
    </>
  );

  const renderForgotPassword = () => (
    <>
      <Header />
      <ErrorBanner />
      <MessageBanner />
      {!message ? (
        <>
          <div className="mb-4 flex items-center">
            <Mail className="w-5 h-5 text-slate-400 mr-2" />
            <p className="text-sm text-slate-600">
              Enter your email and we'll send you a reset link.
            </p>
          </div>
          <form onSubmit={handleForgotPassword} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Email
              </label>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com"
                autoComplete="email"
                className="w-full px-3 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              className="w-full py-2.5 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white rounded-lg font-medium text-sm transition-colors"
            >
              {loading ? "Sending..." : "Send Reset Link"}
            </button>
          </form>
        </>
      ) : null}
      <button
        onClick={() => {
          setView("login");
          setError("");
          setMessage("");
        }}
        className="w-full mt-3 text-sm text-slate-500 hover:text-slate-700 transition-colors flex items-center justify-center"
      >
        <ArrowLeft className="w-3.5 h-3.5 mr-1" /> Back to sign in
      </button>
    </>
  );

  const renderResetPassword = () => (
    <>
      <Header />
      <ErrorBanner />
      {tokenValid === false ? (
        <div className="text-center">
          <button
            onClick={() => {
              setView("forgot");
              setError("");
            }}
            className="mt-2 text-sm text-blue-600 hover:text-blue-800"
          >
            Request a new reset link
          </button>
        </div>
      ) : tokenValid === null ? (
        <p className="text-center text-sm text-slate-500">
          Validating reset link...
        </p>
      ) : (
        <form onSubmit={handleResetPassword} className="space-y-4">
          <p className="text-sm text-slate-600 mb-2">
            Enter your new password.
          </p>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              New Password
            </label>
            <input
              type="password"
              required
              minLength={8}
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="Min 8 characters"
              autoComplete="new-password"
              className="w-full px-3 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Confirm Password
            </label>
            <input
              type="password"
              required
              minLength={8}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Re-enter password"
              autoComplete="new-password"
              className="w-full px-3 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
            />
          </div>
          <button
            type="submit"
            disabled={loading}
            className="w-full py-2.5 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white rounded-lg font-medium text-sm transition-colors"
          >
            {loading ? "Resetting..." : "Reset Password"}
          </button>
        </form>
      )}
    </>
  );

  const renderResetSuccess = () => (
    <>
      <Header />
      <div className="text-center py-4">
        <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-3" />
        <h2 className="text-lg font-medium text-slate-900 mb-1">
          Password Reset
        </h2>
        <p className="text-sm text-slate-600 mb-4">
          Your password has been updated. You can now sign in with your new
          password.
        </p>
        <button
          onClick={() => {
            setView("login");
            setError("");
            setMessage("");
          }}
          className="px-6 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium text-sm transition-colors"
        >
          Sign In
        </button>
      </div>
    </>
  );

  return (
    <div className="min-h-screen bg-slate-100 flex items-center justify-center">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-xl shadow-lg p-8 border border-slate-200">
          {view === "login" && renderLogin()}
          {view === "forgot" && renderForgotPassword()}
          {view === "reset" && renderResetPassword()}
          {view === "reset-success" && renderResetSuccess()}
        </div>
        <p className="text-center text-xs text-slate-400 mt-4">
          Hyve Applied Intelligence
        </p>
        <div className="flex justify-center gap-4 mt-2 text-xs text-slate-400">
          <a
            href="https://hyveappliedintelligence.com/static/marketing/terms.html"
            target="_blank"
            rel="noopener noreferrer"
            className="hover:text-slate-600 transition-colors"
          >
            Terms
          </a>
          <a
            href="https://hyveappliedintelligence.com/static/marketing/privacy.html"
            target="_blank"
            rel="noopener noreferrer"
            className="hover:text-slate-600 transition-colors"
          >
            Privacy
          </a>
          <a
            href="https://hyveappliedintelligence.com/static/marketing/dpa.html"
            target="_blank"
            rel="noopener noreferrer"
            className="hover:text-slate-600 transition-colors"
          >
            DPA
          </a>
        </div>
        <p className="text-center text-xs text-slate-400 mt-3">
          By signing in you agree to our Terms of Service and Privacy Policy.
        </p>
      </div>
    </div>
  );
};

export default LoginPage;
